import React from 'react';
import PropTypes from 'prop-types';
import { isNaN } from 'lodash';

import {
    Chart as ChartJS,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
    CategoryScale,
  } from 'chart.js';
import { Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import Annotation from 'chartjs-plugin-annotation';
import { mean, sum } from 'lodash';
import './style.css';

ChartJS.register(
    LinearScale, 
    PointElement, 
    LineElement, 
    Tooltip, 
    Legend, 
    CategoryScale, 
    ChartDataLabels,
    Annotation,
);


function LineChart({dataSet}){
    // chart variables
    let [results, labels, minRange, maxRange, examItemName, unitOfMeasureBase, upperBound, lowerBound, ] = [[],[],[],[],[],[], 0, 0]; // prepare the buckets
    let [ave, sumOfSquares, variance, sd] = [0, 0, 0, 0]; // variables for setting the bounds of the chart
    let ticksBucket = [-3,-2, -1, 0, 1, 2, 3]; 
    
    // additional chart variables for special cases
    let [hasPreviousResults, inputIsTouched] = [false, false];


    //for styles
    const mainPointColor ="rgba(75,192,192,1)";
    const dangerPointColor = "#E83845";
    const annotationBgColor = 'rgba(245, 245, 245)';
    const initMessage = 'Please click on one of the textboxes on the left pane to show the previous data.';
    const noPrevResultsMessage = 'No previous results.'

    // const regex = /[0-9.,]/g;

    if(dataSet.length > 0){
      dataSet.forEach((val) => {
        //serve the contents into the buckets
        
        const result = Number((val.releasedResult).replace(',',''));
        
        if(!isNaN(result)){
          labels.push(val.approveDate);
          results.push(Number(result));
       }        
        minRange.push(Number(val.rangeLow));
        maxRange.push(Number(val.rangeHigh));
        examItemName.push(val.examItemName);
        unitOfMeasureBase.push(val.unitOfMesureBase);
        hasPreviousResults = val.hasPreviousResults;

        if(!hasPreviousResults){
          labels.push(val.approveDate);
          minRange.push(Number(val.rangeLow));
          maxRange.push(Number(val.rangeHigh));
        }

        inputIsTouched = val.inputIsTouched;
      });

      // stat calculations to fix the chart in a location. 
      ave = mean(results);
      let dataMinusMean = [];
      
      results.forEach( (result) =>{
        dataMinusMean.push( (result - ave)**2 );
      });

      sumOfSquares = sum(dataMinusMean);

      variance = sumOfSquares/results.length;
      sd = variance**(1/2);

      if(sumOfSquares !== 0){
        ticksBucket = [
            ave+(-3*sd),
            ave+(-2*sd), 
            ave+(-1*sd), 
            ave, 
            ave+sd, 
            ave+(2*sd),
            ave+(3*sd),
        ]
      }else{
        ticksBucket = [(-3-ave),
                     (-2-ave),
                     ave,
                     (2+ave),
                     (3+ave),];

        sd = ave;
      }
      lowerBound = ticksBucket[0];    
      upperBound = ticksBucket[ticksBucket.length-1];    
    }

    const chartData = {
      labels: labels,
      datasets :[
        {
          label: examItemName[0] ? examItemName[0] : 'Exam Name',
          data: results,
          backgroundColor: mainPointColor,
          pointBackgroundColor: mainPointColor,
          borderColor: mainPointColor,
          pointStyle: 'circle',
          datalabels: {
            align: 'start',
            anchor: 'start',
          },
        },
        {
          label: "Min Normal Value",
          data: minRange,
          backgroundColor: dangerPointColor,
          borderColor: dangerPointColor,
          pointRadius: false,
          datalabels: {
            align: 'start',
            anchor: 'start',
            display: false,
          }
        },
        {
          label: "Max Normal Value",
          data: maxRange,
          backgroundColor: dangerPointColor,
          borderColor: dangerPointColor,
          pointRadius: false,
          datalabels: {
            align: 'start',
            anchor: 'start',
            display: false,
          }
        }

      ],
    };

  const chartOptions = {
    scales:{
      x:{
        title:{display: true, text:"Date and Time"},
        ticks:{
          callback: function(val, index){
            if(!hasPreviousResults){
              return index % 2 === 0 ? this.getLabelForValue(val) : '' ;
            }else{
              return this.getLabelForValue(val) ;
            }
          },
        }
      },
      y: {
        type:'linear',
        position: 'left',
        title: {
          display: true, 
          text: unitOfMeasureBase[0] ? unitOfMeasureBase[0] : 'Unit of Measure',
        },
        ticks: {
          stepSize: sd,
        },
        min: lowerBound,
        max: upperBound,
        maxTicksLimit: 7,
      },
    },
    responsive: true,
    mainAspectRatio: false,
    plugins: {
        datalabels: {
            backgroundColor: function(context) {
                const { dataIndex, dataset } = context;
                const {data, backgroundColor} = dataset;

                if((data[dataIndex] > maxRange[dataIndex])||(data[dataIndex] < minRange[dataIndex])){
                  return dangerPointColor;
                }            
                else{
                  return backgroundColor;
                }
            },
            borderRadius: 4,
            color: 'white',
            font: {
            weight: 'bold'
            },
            formatter: Math.round(3),
            padding: 6
        },
        annotation: {
          annotations: {
            label1:{
              type: 'label',
              position: 'center',
              backgroundColor: function(){
                if(hasPreviousResults){
                  return 'transparent';
                }else{
                  return annotationBgColor;
                }
              },
              content: function(){
                if(hasPreviousResults || dataSet.length > 0){
                  return null;
                }else if(inputIsTouched || dataSet.length === 0){
                  return noPrevResultsMessage;
                }else{
                  return initMessage;
                }
              },
              font: {
                size: 18
              }
            }
          }
        }
    },
  };
    
  //plot
    return(
    <div>
      <Line 
        // @ts-ignore
        data={chartData}
        // @ts-ignore 
        options={chartOptions}/>
    </div>);
}

LineChart.propTypes ={
    dataSet : PropTypes.array.isRequired,
}

export default LineChart;